import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import SafeIcon from '../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'
import supabase from '../lib/supabase'

function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [logoContent, setLogoContent] = useState({
    text: 'CodeVaro',
    highlight: '.eu',
    image_url: ''
  })
  const location = useLocation()
  const navigate = useNavigate()
  
  useEffect(() => {
    const handleScroll = () => {
      const offset = window.scrollY
      setScrolled(offset > 50)
    }
    
    window.addEventListener('scroll', handleScroll)
    
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])
  
  useEffect(() => {
    // Close mobile menu when route changes
    setIsOpen(false)
  }, [location])
  
  useEffect(() => {
    fetchLogoContent()
  }, [])
  
  const fetchLogoContent = async () => {
    try {
      const { data, error } = await supabase
        .from('codevaro_settings')
        .select('value')
        .eq('setting_key', 'general_content')
        .maybeSingle()
      
      if (error) throw error
      
      if (data && data.value && data.value.logo) {
        setLogoContent(data.value.logo)
      }
    } catch (error) {
      console.error('Error fetching logo content:', error)
    }
  }

  const handleNavClick = (e, sectionId) => {
    e.preventDefault()
    
    if (location.pathname !== '/') {
      // Navigate to home first, then scroll
      navigate('/')
      setTimeout(() => {
        const section = document.getElementById(sectionId)
        if (section) {
          section.scrollIntoView({ behavior: 'smooth' })
        }
      }, 100)
    } else {
      // Already on home, just scroll
      const section = document.getElementById(sectionId)
      if (section) {
        section.scrollIntoView({ behavior: 'smooth' })
      }
    }
    setIsOpen(false)
  }
  
  const navItems = [
    { name: 'Αρχική', sectionId: 'home' },
    { name: 'Υπηρεσίες', sectionId: 'services' },
    { name: 'Λύσεις', sectionId: 'solutions' },
    { name: 'Σχετικά', sectionId: 'about' },
    { name: 'Ραντεβού', sectionId: 'booking' },
    { name: 'Επικοινωνία', sectionId: 'contact' }
  ]
  
  return (
    <header 
      className={`fixed w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-md py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <div>
            <Link to="/" className="flex items-center">
              {logoContent.image_url ? (
                <img 
                  src={logoContent.image_url} 
                  alt="CodeVaro" 
                  className="h-10 w-auto" 
                />
              ) : (
                <div className="text-2xl font-bold text-blue-600 cursor-pointer">
                  {logoContent.text}<span className="text-orange-500">{logoContent.highlight}</span>
                </div>
              )}
            </Link>
          </div>
          
          <nav className="hidden md:flex space-x-8">
            {navItems.map((item) => (
              <a
                key={item.name}
                href={`#${item.sectionId}`}
                onClick={(e) => handleNavClick(e, item.sectionId)}
                className={`text-base font-medium transition-colors ${
                  scrolled 
                    ? 'text-blue-600 hover:text-blue-700 font-bold text-lg' 
                    : 'text-gray-800 hover:text-blue-600 font-bold text-lg'
                }`}
                style={{ color: scrolled ? undefined : 'rgb(247, 128, 24)' }}
              >
                {item.name}
              </a>
            ))}
          </nav>
          
          <div className="md:hidden">
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              className={`inline-flex items-center justify-center p-2 rounded-md ${
                scrolled ? 'text-gray-700 hover:text-blue-600' : 'text-gray-800 hover:text-blue-600'
              }`}
            >
              <SafeIcon 
                icon={isOpen ? FiIcons.FiX : FiIcons.FiMenu} 
                className="h-6 w-6" 
              />
            </button>
          </div>
        </div>
      </div>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="md:hidden bg-white"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 shadow-lg">
              {navItems.map((item) => (
                <a
                  key={item.name}
                  href={`#${item.sectionId}`}
                  onClick={(e) => handleNavClick(e, item.sectionId)}
                  className="block px-3 py-2 rounded-md text-base font-medium text-blue-600 hover:text-blue-700 hover:bg-gray-50"
                >
                  {item.name}
                </a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}

export default Header